import { Card, Col, Row, Tabs, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
	Bar,
	BarChart,
	CartesianGrid,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
	Cell,
} from 'recharts';
import { isMobileOnly } from 'react-device-detect';

import { CHART_DATE_FORMATS, DATE_FORMATTER } from 'utils/constants';
import Utils from 'utils/utils';
import { language } from 'utils/i18n';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { Object } from 'models';
import { selectUser } from 'selectors';

import { Skeleton } from '../index';

import { IChartCardProps } from 'models';

import styles from './index.module.scss';

const FROM: Object = {
	month: moment().subtract(1, 'month').format(DATE_FORMATTER),
	'half-year': moment().subtract(5, 'months').startOf('month').format(DATE_FORMATTER),
	year: moment().subtract(11, 'months').startOf('month').format(DATE_FORMATTER),
};

const INTERVAL: Object = {
	month: 'day',
	'half-year': 'month',
	year: 'month',
	'all-time': 'year',
};

let tooltip = '';

type BarItem = {
	name: string;
	value: number;
	dataKey: string;
	payload: {
		negativeTax?: boolean;
		negativeIncome?: boolean;
		negativeAmount?: boolean;
	};
};

const ChartTooltip = ({ active, payload, label }: any): JSX.Element => {
	if (!active || !tooltip || !payload) return <></>;

	return payload.length > 1 ? (
		<div className={styles.tooltip}>
			<div className={styles.col}>
				{payload.map((bar: BarItem) => {
					const isNegative =
						(bar.dataKey === 'income' && bar.payload.negativeIncome) ||
						(bar.dataKey === 'tax' && bar.payload.negativeTax);
					return (
						<div key={bar.dataKey}>
							<Typography.Text className={styles.amount}>
								{bar.name}: {Utils.moneyFormat(isNegative ? -bar.value : bar.value)}
							</Typography.Text>
						</div>
					);
				})}
			</div>
			<Typography.Text className={styles.label}>{label}</Typography.Text>
		</div>
	) : (
		payload.map((bar: BarItem) => {
			const isNegative = bar.dataKey === 'amount' && bar.payload.negativeAmount;
			return (
				<div className={styles.tooltip} key={bar.dataKey}>
					<Typography.Text className={styles.amount}>
						{Utils.moneyFormat(isNegative ? -bar.value : bar.value)}
					</Typography.Text>
					<Typography.Text className={styles.label}>{label}</Typography.Text>
				</div>
			);
		})
	);
};

export function ChartCard<T>({
	title,
	isProfit,
	total,
	getData,
	subtitle,
	isBar,
	data,
	bars,
	minPointSize,
	helper
}: IChartCardProps<T>) {
	const dispatch = useAppDispatch();
	const user = useAppSelector(selectUser);

	const [type, setType] = useState('month');
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();

	moment.locale(language);

	const {
		monthText,
		sixMonthText,
		yearText,
		sixMonthMobileText,
		yearMobileText,
		monthMobileText,
		noDataYetText,
		allTimeText,
		allTimeMobileText,
	} = useTranslate([
		'monthText',
		'sixMonthText',
		'yearText',
		'sixMonthMobileText',
		'yearMobileText',
		'monthMobileText',
		'noDataYetText',
		'allTimeText',
		'allTimeMobileText',
	]);

	const onGetData = useCallback(
		(type: string) => {
			const from = type === 'all-time' ? user.registrationDate : FROM[type];
			const interval = INTERVAL[type];

			dispatch(
				getData({
					from,
					to: moment().format(DATE_FORMATTER),
					interval,
				}),
			)
				.unwrap()
				.finally(() => {
					setIsLoading(false);
				});
		},
		[dispatch, getData],
	);

	useEffect(() => {
		onGetData(type);
	}, [onGetData]);

	const onChangeType = useCallback(
		(type: string) => {
			setType(type);
			onGetData(type);
		},
		[onGetData],
	);

	const dataValues = useMemo(
		() =>
			data.map((item: any) => {
				const values = item.amount ? { amount: Number(item.amount) } : {};

				return {
					...item,
					...values,
					name: moment(item.date).format(CHART_DATE_FORMATS[type]),
				};
			}),
		[data, type],
	);

	const isZero = total === 0;

	return (
		<Card bordered={false} className={styles.root}>
			<Skeleton loading={isLoading}>
				<>
					<h4 className={classnames(styles.title, { [styles.titleProfit]: isProfit })}>{title}</h4>
					<h5
						className={classnames(styles.subTitle, { [styles.smallSubTitle]: isBar && !isProfit })}
					>
						{subtitle}
					</h5>

					<div className={styles.chart} style={{ width: '100%', height: '240px' }}>
						<ResponsiveContainer>
							<BarChart height={195} data={dataValues}>
								<CartesianGrid stroke="#eee" strokeDasharray="10 10" vertical={false} />
								<XAxis
									dataKey="name"
									stroke="#C9C9C9"
									axisLine={false}
									tickMargin={15}
									tickSize={isMobileOnly ? 7 : 0}
									angle={isMobileOnly ? -45 : 0}
								/>
								<YAxis
									width={46}
									tickMargin={10}
									tickSize={0}
									stroke="#C9C9C9"
									axisLine={false}
									tickFormatter={(value) => {
										value = isZero ? value * 5 : value;
										return Utils.profitFormat(value);
									}}
								/>
								{isZero && <ReferenceLine y={2.4} stroke="null" label={noDataYetText} />}
								{!isZero && (
									<Tooltip
										wrapperStyle={{
											outline: 'none',
										}}
										content={<ChartTooltip active={true} />}
										cursor={{
											stroke: 'none',
											strokeWidth: 1,
											y: 0,
											fill: 'none',
										}}
									/>
								)}

								{bars.map((item) => {
									return (
										<Bar
											minPointSize={minPointSize}
											key={item.dataKey}
											dataKey={item.dataKey}
											name={t(item.name)}
											fill={item.fill}
											barSize={10}
											onMouseOver={() => {
												tooltip = item.dataKey;
											}}
										>
											{dataValues.map((entry, index) => {
												const isYellow =
													(item.dataKey === 'income' && entry.negativeIncome) ||
													(item.dataKey === 'tax' && entry.negativeTax) ||
													(item.dataKey === 'amount' && entry.negativeAmount);
												const hasValue = entry[item.dataKey] > 0;
												return (
													<Cell
														key={`cell-${index}`}
														fill={isYellow ? '#FFC228' : item.fill}
														style={{ visibility: hasValue ? 'initial' : 'hidden' }}
													/>
												);
											})}
										</Bar>
									);
								})}
								{!isZero && <Tooltip />}
							</BarChart>
						</ResponsiveContainer>
					</div>

					<Row justify="center" className={styles.tabs}>
						<Col>
							<Tabs
								type="card"
								onChange={onChangeType}
								defaultActiveKey={type}
								className={styles.desktopTab}
							>
								<Tabs.TabPane tab={monthText} key="month" />
								<Tabs.TabPane tab={sixMonthText} key="half-year" />
								<Tabs.TabPane tab={yearText} key="year" />
								{!isProfit && <Tabs.TabPane tab={allTimeText} key="all-time" />}
							</Tabs>

							<Tabs
								type="card"
								onChange={onChangeType}
								defaultActiveKey={type}
								className={styles.mobileTab}
							>
								<Tabs.TabPane tab={monthMobileText} key="month" />
								<Tabs.TabPane tab={sixMonthMobileText} key="half-year" />
								<Tabs.TabPane tab={yearMobileText} key="year" />
								{!isProfit && <Tabs.TabPane tab={allTimeMobileText} key="all-time" />}
							</Tabs>
						</Col>
					</Row>
				</>
			</Skeleton>
			{helper && <div className={styles.helper}>{helper}</div>}
		</Card>
	);
}
